import { useEffect } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { usePrefetchQuery, useQuery } from '@tanstack/react-query';
import { CenteredSpinner } from '@l2u/web-shared';
import { ProgressBar } from '@l2u/web-shared';
import { CompanyStatusBadge } from '@l2u/web-shared';
import { ProductLink, displayBrandName } from '@l2u/web-shared';
import { NavIcon, type IconName } from '@l2u/web-shared';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { useBranding, useConfirm, useEntityLabels, type EntityLabels } from '@l2u/web-shared';
import { companyApi } from '@l2u/web-shared';
import type { Company } from '@l2u/web-shared';
import { companyPath } from '../lib/audienceRoutes.ts';
import { shellRedirectForAny } from '../lib/lockedRedirect.ts';
import { useQuestionnaireBadgeCount } from '../lib/useVendorActionCount.ts';
import { NavCountBadge, NavCountSrText } from '../components/actionIndicators.tsx';
import { companyNavBadge } from '../lib/companyInfoLock.ts';
import { companyNavCountBadges, companyNavCountsQueryOptions } from '../lib/companyNavCounts.ts';
import {
  companyDashboardQuery,
  expiringDocumentsQuery,
  parseExpiryWindow,
} from '../lib/companyDashboard.ts';

// A function of the tenant's labels rather than a constant: two of these rows
// name entities the tenant may have renamed, and the portal's own nav must not
// disagree with the admin sidebar about what they are called.
function navRows(
  labels: EntityLabels,
): { to: string; label: string; icon: IconName; end?: boolean }[] {
  return [
    { to: companyPath('/dashboard'), label: 'Dashboard', icon: 'dashboard' },
    { to: companyPath('/questionnaire'), label: 'Questionnaire', icon: 'list-checks' },
    { to: companyPath('/training'), label: 'Training', icon: 'graduation-cap' },
    { to: companyPath('/info'), label: `${labels.company} info`, icon: 'building' },
    { to: companyPath('/drivers'), label: labels.drivers, icon: 'users' },
    { to: companyPath('/assets'), label: 'Assets', icon: 'truck' },
    {
      to: companyPath('/corrective-actions'),
      label: 'Corrective actions',
      icon: 'clipboard-check',
    },
    { to: companyPath('/file-notes'), label: 'File notes', icon: 'message-square' },
    { to: companyPath('/documents'), label: 'Documents', icon: 'file-text' },
    { to: companyPath('/library'), label: 'Library', icon: 'book' },
  ];
}

// The Dashboard page is a lazy chunk, so without this its two requests start
// only after the chunk downloads. Mounted by the shell on the Dashboard only,
// it starts them beside the download instead; the page's own
// `refetchOnMount: 'always'` observer then dedupes onto the in-flight fetch
// (TanStack returns the running fetch's promise when there is no data to
// cancel). usePrefetchQuery prefetches during render and only for a key with
// no cache entry yet — a return visit has the chunk already, so the page's own
// refetch starts at once.
function DashboardPrefetch({ days }: { days: number }) {
  usePrefetchQuery(companyDashboardQuery());
  usePrefetchQuery(expiringDocumentsQuery(days));
  return null;
}

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase() || 'U';
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() || 'U';
}

export function CompanyShell() {
  const { session, loading, signOut } = useCompanyAuth();
  const confirm = useConfirm();
  const labels = useEntityLabels();
  const NAV = navRows(labels);

  // The portal has no password, so signing out costs a round trip through the
  // inbox to get back in. On mobile the sign-out icon was the ONLY control in
  // the header, sitting immediately beside the logo — a one-tap accident at all
  // times, and until now the page it led to was a dead end.
  async function confirmSignOut() {
    const ok = await confirm({
      title: 'Sign out?',
      body: 'You will need a new sign-in link emailed to you to get back in.',
      confirmLabel: 'Sign out',
    });
    if (ok) await signOut();
  }
  const { branding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const brandName = displayBrandName(branding.companyName);

  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => companyApi.get<{ company: Company }>('/api/me', { signal }),
    enabled: !!session,
  });
  const questionnaireTo = companyPath('/questionnaire');
  // A published company-information Not Compliant (0060) badges the Company
  // info item from the ['me'] cache. Kept apart from actionCount, which is
  // question-scoped and shared with the driver portal.
  // Training, Drivers, Assets, Corrective actions, File notes and Library: one
  // aggregated request (GET /api/me/nav-counts) rather than each page's list.
  // Fetched in the background — nothing waits on it, and a failure just shows
  // no badges.
  const navCounts = useQuery({ ...companyNavCountsQueryOptions(), enabled: !!session });
  // Items the company still has to action, across every questionnaire — the
  // red pill on the Questionnaire nav item. Counted by the API (GET
  // /api/me/action-count, in the background), so the shell no longer keeps the
  // full question and answer lists live on every page to draw it; the wizard
  // refetches it when its own lists move the count. The lists are fetched
  // here only if that endpoint fails.
  const badge = useQuestionnaireBadgeCount('company', !!session);
  const actionCount = badge.count ?? 0;
  const badgeCtx = {
    questionnaireTo,
    infoTo: companyPath('/info'),
    actionCount,
    company: data?.company,
    countBadges: companyNavCountBadges(
      navCounts.data?.counts,
      {
        trainingTo: companyPath('/training'),
        driversTo: companyPath('/drivers'),
        assetsTo: companyPath('/assets'),
        correctiveTo: companyPath('/corrective-actions'),
        libraryTo: companyPath('/library'),
        fileNotesTo: companyPath('/file-notes'),
      },
      labels,
    ),
  };
  const badgeFor = (to: string) => companyNavBadge(to, badgeCtx).count;
  const srTextFor = (to: string) => companyNavBadge(to, badgeCtx).srText;

  // A 401 is a lapsed session -> /expired; `account_locked` (a 403) is a
  // questionnaire deadline the nightly run locked -> /locked. Either way the
  // session is useless here, so it is dropped. See lib/lockedRedirect.ts.
  // Read off ALL the shell's own queries (same middleware), whichever lands
  // first — /me no longer gates the page, so it may well not be first.
  const redirectTo = shellRedirectForAny([error, navCounts.error, badge.error], 'company');
  useEffect(() => {
    if (redirectTo) {
      void signOut();
      navigate(companyPath(redirectTo), { replace: true });
    }
  }, [redirectTo, signOut, navigate]);

  if (loading) return <CenteredSpinner />;
  // No session at all is someone who has not signed in yet — most often a
  // visitor who typed the bare hostname — so they go to the sign-in form. The
  // "your link has expired" page is only for a session the API actually
  // REJECTED, which the 401 effect above handles; showing it to a first-time
  // visitor told them about a link they never had.
  if (!session) {
    return <Navigate to={companyPath('/login')} state={{ from: location }} replace />;
  }
  // Nothing else waits for /api/me. The shell used to hold the WHOLE page
  // behind a spinner until it answered, so every page's own queries started
  // one round trip late; now the chrome renders with placeholders for the bits
  // that need the company, and <Outlet/> mounts at once so the page fetches in
  // parallel. Each page gates on its own queries, and the API refuses every
  // one of them for a locked or deactivated account (companyAuth), so the page
  // never has data this shell would have hidden.
  //
  // The one gate kept: once any of the shell's queries has come back as a
  // lapsed session or a locked account, the effect above is about to sign out
  // and redirect — show the spinner rather than flash the page's own error
  // state for that frame.
  if (redirectTo) return <CenteredSpinner />;
  const company = data?.company;

  return (
    <div className="flex min-h-screen bg-white">
      {location.pathname === companyPath('/dashboard') && (
        <DashboardPrefetch
          days={parseExpiryWindow(new URLSearchParams(location.search).get('days'))}
        />
      )}
      {/* `md:h-dvh` bounds the aside to one viewport instead of letting it
        stretch to the page height — without that bound `flex-1` hands the nav
        all the room it asks for and its `overflow-y-auto` never engages, so the
        sign-out footer rides off the bottom of a long page. `md:sticky md:top-0`
        pins it while the window scrolls the main column; `sticky` (not `fixed`)
        keeps the aside in flow so <main> still gets its width from the flex row.
        Every child below is `shrink-0` except <nav>, so the menu is the only
        thing that scrolls. */}
      <aside
        className="relative hidden w-72 shrink-0 flex-col border-r md:sticky md:top-0 md:flex md:h-dvh"
        style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
      >
        {/* Brand */}
        <div className="flex shrink-0 items-center px-6 pb-5 pt-6">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={brandName} className="h-10 w-auto" />
          ) : (
            <span
              className="text-[15px] font-bold tracking-tight"
              style={{ color: 'var(--brand-primary)' }}
            >
              {brandName}
            </span>
          )}
        </div>

        {/* Company status card — a same-sized placeholder while /api/me loads,
            so the nav below does not jump when it lands. */}
        {!company && isLoading && (
          <div
            aria-hidden="true"
            data-testid="company-card-skeleton"
            className="mx-5 mb-4 shrink-0 animate-pulse rounded-lg border bg-white p-3"
            style={{ borderColor: '#E2E8F0' }}
          >
            <div className="h-[18px] w-2/3 rounded bg-slate-100" />
            <div className="mt-1.5 h-5 w-24 rounded-full bg-slate-100" />
            <div className="mt-3 h-8 rounded bg-slate-100" />
          </div>
        )}
        {company && (
          <div
            className="mx-5 mb-4 shrink-0 rounded-lg border bg-white p-3"
            style={{ borderColor: '#E2E8F0' }}
          >
            <div className="truncate text-[13px] font-semibold" style={{ color: '#0F172A' }}>
              {company.company_name}
            </div>
            <div className="mt-1.5">
              <CompanyStatusBadge status={company.status} audience="vendor" />
            </div>
            <div className="mt-3">
              <ProgressBar value={company.progress} label="Progress" />
            </div>
          </div>
        )}

        {/* Nav — the only scrollable region of the sidebar. `min-h-0` defeats
          the automatic minimum size a flex item gets from its content, which
          would otherwise let a long nav push the footer out of the aside. */}
        <nav className="min-h-0 flex-1 overflow-y-auto px-4 pb-2">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                `relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition ${
                  isActive ? 'font-semibold' : 'font-medium hover:bg-brand-hover'
                }`
              }
              // Tenant wash + contrast-clamped on-wash text (brandTints), the
              // same pair as the admin sidebar. This was a fixed blue
              // (#DBEAFE / #1D4ED8) that ignored the tenant's brand entirely.
              style={({ isActive }) =>
                isActive
                  ? { background: 'var(--brand-active-bg)', color: 'var(--brand-active-text)' }
                  : { color: '#475569' }
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative flex">
                    <NavIcon
                      name={item.icon}
                      size={17}
                      style={{ color: isActive ? 'var(--brand-active-text)' : '#64748B' }}
                    />
                    {/* Visual only: the announcement follows the label below. */}
                    <NavCountBadge count={badgeFor(item.to)} announce={false} />
                  </span>
                  <span className="flex-1 text-left">
                    {item.label}
                    <NavCountSrText
                      count={badgeFor(item.to)}
                      {...(srTextFor(item.to) ? { text: srTextFor(item.to)! } : {})}
                    />
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="shrink-0 border-t px-4 pb-4 pt-3" style={{ borderColor: '#E2E8F0' }}>
          <div className="flex items-center gap-2 px-2 pb-3">
            <span
              className="h-[7px] w-[7px] rounded-full"
              style={{
                background: '#10B981',
                boxShadow: '0 0 8px rgba(16,185,129,0.5)',
              }}
            />
            <span className="flex-1 text-[11.5px]" style={{ color: '#475569' }}>
              All systems normal
            </span>
          </div>
          <button
            type="button"
            onClick={() => void confirmSignOut()}
            className="flex w-full items-center gap-3 rounded-lg border bg-white p-2 text-left transition hover:bg-slate-50"
            style={{ borderColor: '#E2E8F0' }}
            title="Sign out"
          >
            <div
              className="font-mono-eyebrow flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
              style={{ background: 'var(--brand-primary)' }}
            >
              {initials(company?.contact_name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12.5px] font-semibold" style={{ color: '#0F172A' }}>
                {!company && isLoading ? (
                  <span
                    aria-hidden="true"
                    className="inline-block h-3 w-24 animate-pulse rounded bg-slate-100 align-middle"
                  />
                ) : (
                  (company?.contact_name ?? 'Account')
                )}
              </div>
              <div className="truncate text-[10.5px]" style={{ color: '#64748B' }}>
                Sign out
              </div>
            </div>
            <NavIcon name="log-out" size={16} style={{ color: '#64748B' }} />
          </button>
          <div
            className="font-mono-eyebrow mt-3 text-center text-[10px] uppercase tracking-[0.2em]"
            style={{ color: '#64748B' }}
          >
            Powered by <ProductLink style={{ color: 'var(--brand-primary)' }} />
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col" style={{ background: '#F8FAFC' }}>
        {/* Mobile top bar */}
        <header
          className="flex items-center justify-between border-b px-4 py-3 md:hidden"
          style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
        >
          <div className="flex min-w-0 items-center">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={brandName} className="h-8 w-auto" />
            ) : (
              <span
                className="truncate text-[14px] font-bold tracking-tight"
                style={{ color: 'var(--brand-primary)' }}
              >
                {brandName}
              </span>
            )}
          </div>
          {/* Was an unlabelled icon button right beside the logo — the only
              control in the mobile header, and one tap from locking yourself
              out. Now a labelled, confirmed, secondary-weight control. */}
          <button
            type="button"
            onClick={() => void confirmSignOut()}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium"
            style={{ borderColor: '#E2E8F0', color: '#64748B' }}
          >
            <NavIcon name="log-out" size={14} style={{ color: '#94A3B8' }} />
            Sign out
          </button>
        </header>

        {/* Mobile nav tabs */}
        <nav
          className="flex gap-1 overflow-x-auto border-b px-4 md:hidden"
          style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                `whitespace-nowrap border-b-2 px-3 py-3 text-sm transition ${
                  isActive ? 'font-semibold' : 'border-transparent font-medium'
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? { borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }
                  : { color: '#475569' }
              }
            >
              {item.label}
              <NavCountBadge
                count={badgeFor(item.to)}
                inline
                {...(srTextFor(item.to) ? { srText: srTextFor(item.to)! } : {})}
              />
            </NavLink>
          ))}
        </nav>

        <main className="w-full flex-1 px-8 pb-8 pt-5">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
